// Importing the Dexie library
import Dexie from 'dexie';

// Creating a new Dexie database instance named 'web3Dmails'
const db = new Dexie('web3Dmails');

// Defining the structure of the database with a single table named 'emails'
db.version(1).stores({
    inbox: '++id, mailId, decryptedMail , type' ,
    sent: '++id, mailId, decryptedMail , type' ,
    trash: '++id, mailId, decryptedMail , type',
    insecure: '++id, mailId, decryptedMail , type',
    outbox: '++id, json',
    attachment: '++id, name, s3url, file',
    folders: '++id, mailId, decryptedMail , type',
    important: '++id, mailId, decryptedMail , type',
    senttrash: '++id, mailId, decryptedMail , type',
    archive: '++id, mailId, decryptedMail , type',
    draft: '++id, json',
    organizationData: '++id, data',
});

// Version 2 — the mail cache is now encrypted at rest, and rows are owner-tagged.
//
// Two changes, both required before the cache is allowed to SURVIVE a sign-in (see routes/SignIn.js):
//
//  1. `decryptedMail` no longer holds plaintext — it holds a blob encrypted under the dMail DEK
//     (helper/dekManager.js), tagged by `cacheVersion`. It is therefore dropped from the index list:
//     indexing ciphertext is useless, and it was never queried by value. Rows written before this
//     version have no `cacheVersion` and are still read back as plaintext (see indexed-helper.js) so
//     existing installs keep working; they are replaced with encrypted rows as they are re-read.
//
//  2. `userName` is indexed on every mail table. The cache is keyed by mail id, which is only unique
//     PER USER — without an owner tag one account's cached mail could be served to the next account
//     signed in on the same browser.
//
// `cacheMeta` records which account the cache currently belongs to. It cannot live in localStorage:
// SignIn.checkAuth() clears localStorage before the account is chosen, so the owner must be stored
// alongside the data it describes.
const mailStore = '++id, mailId, type, userName';
db.version(2).stores({
    inbox: mailStore,
    sent: mailStore,
    trash: mailStore,
    insecure: mailStore,
    folders: mailStore,
    important: mailStore,
    senttrash: mailStore,
    archive: mailStore,
    cacheMeta: 'key',
});

// Version 3 — the three remaining plaintext stores are encrypted at rest too (db/at-rest.js).
//
// Version 2 encrypted the mail cache and stopped sign-in from wiping the database for the same user.
// That left `attachment` (decrypted file bytes), `outbox` and `draft` (the user's own composed body) as
// readable rows which nothing scrubbed any more — the wipe had been their only eraser. Same envelope as
// the mail tables now: `file` / `json` hold a blob encrypted under the dMail DEK, tagged by
// `cacheVersion`, owner-tagged by `userName`. Legacy rows have neither and are read as plaintext once,
// then rewritten encrypted (see db/at-rest.js).
//
// Index changes:
//  - `file` / `json` / `name` are dropped from the index lists: they hold ciphertext now (or, for
//    `name`, moved inside the envelope), and none of them was ever queried by value.
//  - `userName` is indexed, as on the mail tables — these stores are keyed by an id or an s3url, neither
//    of which is unique per account.
//  - `[s3url+userName]` lets the attachment prefetch test "already cached for THIS user" against the
//    index alone, without loading the attachment bodies it exists to avoid loading.
//
// Still no `&s3url` unique index: existing installs contain duplicate s3url rows (the add() bug fixed in
// helper/attachmentHelper.js), so the constraint would throw ConstraintError during the upgrade and leave
// the whole database unopenable. De-duplication stays on the write path (at-rest.putAttachmentFile),
// which converges existing installs without risking the upgrade.
db.version(3).stores({
    attachment: '++id, s3url, userName, [s3url+userName]',
    outbox: '++id, userName',
    draft: '++id, userName',
});

// Version 4 — the last plaintext store, `organizationData`, is encrypted at rest too (db/at-rest.js).
//
// The row was the raw /getOrganizationData response spread across its own columns: primaryEmail,
// organizationSettings, userSettingsData. Version 2 stopped sign-in from wiping the database for the same
// user, which left this readable on disk indefinitely — the wipe had been its only eraser.
//
// The payload now lives sealed in `data`, tagged by `cacheVersion` and owner-tagged by `userName`, exactly
// like `json` on outbox/draft. `data` leaves the index list because it holds ciphertext and was never
// queried by value.
//
// `uuid` / `organizationId` remain plain columns (unindexed): helper/txSigner.js resolveTier() reads them
// to pick the signing path, and that must work without a DEK. They are opaque server identifiers, not user
// content — the same carve-out `s3url` gets on the attachment store.
//
// Legacy rows have no cacheVersion and are read as plaintext once, then rewritten sealed.
db.version(4).stores({
    organizationData: '++id, userName',
});

// Exporting the Dexie database instance to be used in other modules
export default db;
